import React, { useState, useCallback } from 'react';
import texasApi from '../../services/texasApi';
import { RekeyUnit } from '../../services/texasComplianceService';

export const RekeyCountdown: React.FC = () => {
  const [propertyId, setPropertyId] = useState<string>('');
  const [tenantId, setTenantId] = useState<string>('');
  const [ledger, setLedger] = useState<RekeyUnit[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // UX-9: "Load Ledger" used to silently no-op when no property/tenancy was
  // selected. Surface a prompt so the user knows they need to create/select one.
  const [notice, setNotice] = useState<string | null>(null);
  const [activating, setActivating] = useState(false);
  const [evidenceInput, setEvidenceInput] = useState<string>('');
  const [uploadingEvidence, setUploadingEvidence] = useState(false);
  // UX: "Evaluate" produced no on-screen result — the deadline evaluation is now
  // rendered (state, days remaining, overdue flag, penalty warning) instead of
  // only being logged to the console.
  const [evaluation, setEvaluation] = useState<any | null>(null);

  const fetchLedger = useCallback(async (pid: string, tid?: string) => {
    if (!pid) {
      setNotice('Select or create a property and tenancy first, then enter its Property ID to load the ledger.');
      return;
    }
    setLoading(true);
    setError(null);
    setNotice(null);
    try {
      const res = await texasApi.rekeyLedger.getLedger(pid);
      const data = tid ? await texasApi.rekeyLedger.getTenantLedger(pid, tid) : res;
      setLedger(Array.isArray(data) ? data : [data]);
    } catch (err: any) {
      setError(err?.message || 'Failed to load ledger');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleActivate = async () => {
    if (!propertyId.trim()) return;
    setActivating(true);
    try {
      await texasApi.rekeyLedger.activate({ propertyId: propertyId.trim() });
      await fetchLedger(propertyId.trim());
    } catch (err: any) {
      setError(err?.message || 'Activation failed');
    } finally {
      setActivating(false);
    }
  };

  const handleUploadEvidence = async () => {
    if (!propertyId.trim() || !tenantId.trim() || !evidenceInput.trim()) return;
    setUploadingEvidence(true);
    try {
      await texasApi.rekeyLedger.recordReceipt(propertyId.trim(), tenantId.trim(), { evidence: evidenceInput });
      setEvidenceInput('');
      await fetchLedger(propertyId.trim(), tenantId.trim());
    } catch (err: any) {
      setError(err?.message || 'Evidence upload failed');
    } finally {
      setUploadingEvidence(false);
    }
  };

  const handleEvaluate = async () => {
    if (!propertyId.trim() || !tenantId.trim()) return;
    setLoading(true);
    setError(null);
    setNotice(null);
    setEvaluation(null);
    try {
      const res = await texasApi.rekeyLedger.evaluate(propertyId.trim(), tenantId.trim());
      const payload: any = (res as any)?.data ?? res;
      const result: any = payload?.data ?? payload;
      setEvaluation(result);
    } catch (err: any) {
      setError(err?.message || 'Failed to evaluate deadline');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="rekey-countdown" className="texas-page-container">
      <h1>7-Day Re-Key Countdown</h1>
      <p className="page-description">
        Track and manage 7-day re-keying deadlines with evidence upload.
      </p>

      <div className="search-bar">
        <input
          type="text"
          placeholder="Property ID"
          value={propertyId}
          onChange={(e) => setPropertyId(e.target.value)}
          data-testid="property-id-input"
        />
        <input
          type="text"
          placeholder="Tenant ID (optional)"
          value={tenantId}
          onChange={(e) => setTenantId(e.target.value)}
          data-testid="tenant-id-input"
        />
        <button onClick={() => fetchLedger(propertyId.trim(), tenantId.trim() || undefined)} disabled={loading} data-testid="load-btn">
          {loading ? 'Loading...' : 'Load Ledger'}
        </button>
      </div>

      {error && <div className="error" data-testid="error">{error}</div>}

      {notice && (
        <div className="rekey-notice" data-testid="load-ledger-notice">
          <i className="fas fa-exclamation-circle mr-2"></i>
          {notice}
        </div>
      )}

      <div className="action-bar">
        <button onClick={handleActivate} disabled={activating || !propertyId.trim()} data-testid="activate-btn">
          {activating ? 'Activating...' : 'Activate Re-key'}
        </button>
        <button onClick={handleEvaluate} disabled={!propertyId.trim() || !tenantId.trim()} data-testid="evaluate-btn">
          Evaluate
        </button>
      </div>

      {evaluation && (
        <div className="evaluation-section ledger-section" data-testid="evaluate-result">
          <h2>Deadline Evaluation</h2>
          <table className="ledger-table">
            <tbody>
              <tr>
                <td>Status</td>
                <td data-testid="evaluate-state">{evaluation.state || evaluation.status || 'UNKNOWN'}</td>
              </tr>
              <tr>
                <td>Days Remaining</td>
                <td data-testid="evaluate-days-remaining">{evaluation.daysRemaining ?? 'N/A'}</td>
              </tr>
              <tr>
                <td>Overdue</td>
                <td data-testid="evaluate-overdue">{evaluation.isOverdue ? 'Yes' : 'No'}</td>
              </tr>
              {(evaluation.deadlineAt || evaluation.deadline || evaluation.rekeyDeadline) && (
                <tr>
                  <td>Deadline</td>
                  <td>{evaluation.deadlineAt || evaluation.deadline || evaluation.rekeyDeadline}</td>
                </tr>
              )}
            </tbody>
          </table>
          {evaluation.penaltyWarningRequired && evaluation.statutoryPenalty && (
            <p className="evaluation-penalty" data-testid="evaluate-penalty">
              {evaluation.statutoryPenalty}
            </p>
          )}
        </div>
      )}

      {ledger.length > 0 && (
        <div className="ledger-section" data-testid="ledger-section">
          <h2>Re-Key Ledger</h2>
          <table className="ledger-table">
            <thead>
              <tr>
                <th>Unit</th>
                <th>Status</th>
                <th>Deadline</th>
                <th>Days Remaining</th>
              </tr>
            </thead>
            <tbody>
              {ledger.map((unit: RekeyUnit, idx: number) => (
                <tr key={idx} data-testid={`ledger-unit-${idx}`}>
                  <td>{unit.unitId || unit.propertyId}</td>
                  <td>{unit.status}</td>
                  <td>{unit.rekeyDeadline}</td>
                  <td>{unit.daysRemaining ?? 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="evidence-section">
        <h3>Upload Evidence</h3>
        <textarea
          placeholder="Evidence description or URL"
          value={evidenceInput}
          onChange={(e) => setEvidenceInput(e.target.value)}
          data-testid="evidence-input"
        />
        <button
          onClick={handleUploadEvidence}
          disabled={uploadingEvidence || !propertyId.trim() || !tenantId.trim() || !evidenceInput.trim()}
          data-testid="upload-evidence-btn"
        >
          {uploadingEvidence ? 'Uploading...' : 'Upload Evidence'}
        </button>
      </div>
    </div>
  );
};

export default RekeyCountdown;
